import {
  FC, useCallback, useEffect, useMemo, useState, 
} from 'react';
import { useTranslation } from 'react-i18next';

import useMaskedInput from 'hooks/useMaskedInput';
import useCurrency from 'hooks/useCurreny';
import Input from 'components/Input';
import Select from 'components/Select';

import resetIcon from '/resetIcon.svg';

import {
  cmToFineDry, numericValues, unMaskInput, unMaskInputValue, 
} from 'utils/helpers';
import { BoardSoil } from 'features/Croptimizer/types';
import MaskInput from 'components/MaskedInput';
import { BoardSteps } from 'utils/constants';
import { TypeOfKey } from 'utils/types';
import { useNextHandler } from 'features/Croptimizer/providers/NextHandlerContext';
import { scrollTop } from 'utils/scroll-top';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import { formatSwissNumber } from 'utils/formatNumber';

import {
  getEfficiencyFormula,
  getFineDrySoilFirst,
  getFineDrySoilSecond,
  getModifiedTotalMtha,
  getNotModifiedTotalMtha,
  getP39Value,
} from '../../../../../../utils/calcHelperBoard';

interface Props {
  isPDF?: boolean;
}

const splitOptions = [
  {
    value: '1',
    label: '1',
  },
  {
    value: '2',
    label: '2',
  },
  {
    value: '3',
    label: '3',
  },
  {
    value: '4',
    label: '4',
  },
  {
    value: '5',
    label: '5',
  },
];

const SoilBoard: FC<Props> = ({ isPDF }) => {
  const {
    setStep,
    boardStatusValues,
    mineralizationValues,
    setBoardSoilValues,
    boardSoilValues,
    step,
    mtHa,
    onlyMt,
    onlyAc,
    products,
    efficiency,
  } = useCroptimizer();

  const isActive = step === BoardSteps.soil;

  const handleNextStep = () => {
    setStep(BoardSteps.crop);
  };

  const handleBackStep = () => {
    setStep(BoardSteps.status);
    scrollTop();
  };

  const changeBoardSoilValues = useCallback(
    (name: keyof BoardSoil) => (value: TypeOfKey<BoardSoil>) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardSoilValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
    },
    [],
  );

  const { t } = useTranslation();
  const { setDisabled, setHandler, setShowBack, setShowNext, setBackHandler } =
    useNextHandler();

  const { value: productType, setValue: setProductType } = useMaskedInput({
    defaultValue: '',
    value: boardSoilValues?.productName ?? '',
    onChangeCallback: (value) => {
      changeBoardSoilValues('productName')(value);
      const getProduct = products?.find(
        (product) => product.Products === value,
      );
      setNv(String(getProduct?.NV));
      setInitialNv(String(getProduct?.NV));
    },
    isDisableClean: true,
  });

  const { currencyGreen } = useCurrency();

  const {
    value: nv,
    setValue: setNv,
    clean: cleannv,
  } = useMaskedInput({
    defaultValue: '',
    value: boardSoilValues?.nv ?? '',
    onChangeCallback: changeBoardSoilValues('nv'),
  });

  const {
    value: mt,
    setValue: setMt,
    clean: cleanmt,
  } = useMaskedInput({
    defaultValue: '',
    value: boardSoilValues?.mt ?? '',
    onChangeCallback: changeBoardSoilValues('mt'),
  });

  const [initialNv, setInitialNv] = useState<string>(boardSoilValues?.nv ?? '');

  const {
    value: service,
    setValue: setService,
    clean: cleanservice,
  } = useMaskedInput({
    defaultValue: '',
    value: boardSoilValues?.service ?? '',
    onChangeCallback: changeBoardSoilValues('service'),
  });

  const {
    value: dose,
    setValue: setDose,
    clean: cleandose,
  } = useMaskedInput({
    defaultValue: '100',
    value: boardSoilValues?.dose ?? '100',
    onChangeCallback: changeBoardSoilValues('dose'),
  });

  const { value: split, setValue: setSplit } = useMaskedInput({
    value: boardSoilValues?.split ?? '1',
    defaultValue: '1',
    onChangeCallback: changeBoardSoilValues('split'),
    isDisableClean: true,
  });

  const isGreaterTarget = useMemo(() => {
    if (boardStatusValues) {
      return boardStatusValues?.currentPh < boardStatusValues?.targetPh;
    }

    return false;
  }, [boardStatusValues]);

  const productSelect = useMemo(() => {
    if (products) {
      return products?.map((product) => ({
        value: product.Products,
        label: product.Products,
      }));
    }

    return [];
  }, [products]);

  useEffect(() => {
    if (!productType && productSelect) {
      setProductType(productSelect?.[0]?.value);
      const getProduct = products?.find(
        (product) => product.Products === productSelect?.[0]?.value,
      );
      setNv(String(getProduct?.NV));
      setInitialNv(String(getProduct?.NV));
    }

    return;
  }, [productSelect]);

  const errorNv = useMemo(() => {
    if (Number(cleannv) < 10) {
      return t('minimumPossibleValue') + ' 10';
    }

    if (Number(cleannv) > 100) {
      return t('maximumPossibleValue') + ' 100';
    }

    return null;
  }, [nv]);

  const errorMt = useMemo(() => {
    if (Number(cleanmt) < 0) {
      return t('minimumPossibleValue') + ' 0';
    }

    if (Number(cleanmt) > 999999) {
      return t('maximumPossibleValue') + ' 999\'999';
    }

    return null;
  }, [mt]);

  const errorDose = useMemo(() => {
    if (Number(cleandose) < 10) {
      return t('minimumPossibleValue') + ' 10%';
    }

    if (Number(cleandose) > 300) {
      return t('maximumPossibleValue') + ' 300%';
    }

    return null;
  }, [dose]);

  const getSelectedValue = useMemo(() => {
    if (productType?.length > 0 && products) {
      const getProduct = products?.find(
        (product) => product.Products === productType,
      );

      return String(getProduct?.CAP);
    }

    return '';
  }, [productType, products]);

  const getOptimumValue = useMemo(() => {
    if (boardStatusValues && efficiency?.length) {
      const targetSec = Number(numericValues(boardStatusValues?.targetSec));

      // Filter out items with null CAP values
      const validEfficiency = efficiency.filter((item) => item.CAP !== null);

      if (validEfficiency.length === 0) {
        return '';
      }

      if (targetSec < 40) {
        return String(
          validEfficiency.reduce((acc, loc) => (acc.CAP < loc.CAP ? acc : loc))
            .CAP,
        );
      }

      if (targetSec > 150) {
        return String(
          validEfficiency.reduce((acc, loc) => (acc.CAP > loc.CAP ? acc : loc))
            .CAP,
        );
      }

      return String(
        validEfficiency.reduce((a, b) => Math.abs(b.CAP - targetSec) < Math.abs(a.CAP - targetSec) ? b : a,
        ).CAP,
      );
    }

    return '';
  }, [boardStatusValues, efficiency]);

  const getTotalMthaText = useMemo(() => {
    if (boardStatusValues?.currentPh && boardStatusValues?.targetPh) {
      const text = isGreaterTarget ? `Total ${mtHa}` : 'Kg/ha/yr';

      return text;
    }

    return '';
  }, [boardStatusValues]);

  const getMthaText = useMemo(() => {
    if (boardStatusValues?.currentPh && boardStatusValues?.targetPh) {
      const text = `Total VN (CaO) ${'in'} ${mtHa}*`;

      /* const text = isGreaterTarget
        ? `Total ${mtHa}*` + split
        : 'Kg/ha/yr/' + split; */

      return text;
    }

    return '';
  }, [boardStatusValues, split]);

  const getTotalMtha = useMemo(() => {
    if (boardStatusValues?.currentPh && boardStatusValues?.targetPh) {
      const CECSoil = Number(unMaskInput(boardSoilValues?.secsmol ?? '') || 0);
      const FineAndDry = cmToFineDry(Number(boardSoilValues?.fineDrySoil ?? '20')); 
      const NV = nv.length > 0 ? Number(cleannv) : 1;
      const TargetStatus = Number(boardStatusValues?.targetPh);
      const CurrentStatus = Number(boardStatusValues?.currentPh);
      const Dose = Number(cleandose) / 100;

      if (isGreaterTarget) {
        const currentSecRaw = boardStatusValues?.currentSec ?? '';
        const autoSecRaw = boardStatusValues?.initialCurrentSec ?? '';
        const currentSecNumber = Number(unMaskInput(currentSecRaw));
        const usesBaseFormula = currentSecRaw !== ''
          && autoSecRaw !== ''
          && currentSecNumber === Number(unMaskInput(autoSecRaw));

        if (usesBaseFormula) {
          return getNotModifiedTotalMtha(
            CECSoil,
            FineAndDry,
            NV,
            TargetStatus,
            CurrentStatus,
            Dose,
          );
        }

        return getModifiedTotalMtha(
          currentSecNumber,
          CurrentStatus,
          CECSoil,
          FineAndDry,
          NV,
          TargetStatus,
          Dose,
        );
      } else {
        return getP39Value(CECSoil, NV, Dose);
      }
    }

    return '';
  }, [
    boardStatusValues?.currentPh,
    boardStatusValues?.currentSec,
    boardStatusValues?.initialCurrentSec,
    boardStatusValues?.targetPh,
    dose,
    boardSoilValues?.fineDrySoil,
    nv,
    boardSoilValues?.secsmol,
  ]);

  const getMtha = useMemo(() => {
    if (getTotalMtha && split && boardStatusValues) {
      const text = isGreaterTarget
        ? String(Number(getTotalMtha) / Number(split))
        : String(Number(getTotalMtha) * Number(split));

      return text;
    }

    return '';
  }, [getTotalMtha, split, boardStatusValues]);

  const getFineDrySoil = useMemo(() => {
    if (getTotalMtha && split && boardStatusValues && !!cleannv) {
      const CECSoil = Number(unMaskInput(boardSoilValues?.secsmol ?? '') || 0);
      const FineAndDry = cmToFineDry(Number(boardSoilValues?.fineDrySoil ?? '20')); 
      const NV = Number(cleannv ?? 1);
      const TargetStatus = Number(boardStatusValues?.targetPh);
      const CurrentStatus = Number(boardStatusValues?.currentPh);
      const Dose = Number(cleandose) / 100;

      if (isGreaterTarget) {
        const v33 = () => {
          const currentSecRaw = boardStatusValues?.currentSec ?? '';
          const autoSecRaw = boardStatusValues?.initialCurrentSec ?? '';
          const currentSecNumber = Number(unMaskInput(currentSecRaw));
          const usesBaseFormula = currentSecRaw !== ''
            && autoSecRaw !== ''
            && currentSecNumber === Number(unMaskInput(autoSecRaw));

          if (usesBaseFormula) {
            return getNotModifiedTotalMtha(
              CECSoil,
              FineAndDry,
              NV,
              TargetStatus,
              CurrentStatus,
              Dose,
            );
          }

          return getModifiedTotalMtha(
            currentSecNumber,
            CurrentStatus,
            CECSoil,
            FineAndDry,
            NV,
            TargetStatus,
            Dose,
          );
        };

        return getFineDrySoilFirst(
          Math.ceil(Number(v33()) * 100) / 100,
          Number(cleanmt),
          Number(cleanservice),
        );
      } else {
        return getFineDrySoilSecond(
          Number(getTotalMtha),
          Number(cleanmt),
          Number(cleanservice),
        );
      }
    }

    return '';
  }, [
    getTotalMtha,
    split,
    boardStatusValues,
    boardSoilValues?.secsmol,
    boardSoilValues?.fineDrySoil,
    nv,
    dose,
    mt,
    service,
  ]);

  const efficiencyValue = useMemo(() => {
    if (mineralizationValues && boardStatusValues) {
      if (mineralizationValues?.cap === t('no')) {
        return 'Off';
      } else {
        return getEfficiencyFormula(
          Number(mineralizationValues?.month),
          Number(boardStatusValues?.currentPh),
          Number(unMaskInput(boardStatusValues?.currentSec ?? '')),
          Number(getSelectedValue),
          Number(numericValues(mineralizationValues?.soilTillage)),
        );
      }
    }

    return 'Off';
  }, [mineralizationValues, boardStatusValues, getSelectedValue]);

  const resetData = () => {
    if (productSelect) {
      setProductType(productSelect?.[0]?.value);
    }

    if (productType?.length > 0 && products) {
      const getProduct = products?.find(
        (product) => product.Products === productType,
      );
      setNv(String(getProduct?.NV));
      setMt('');
      setInitialNv(String(getProduct?.NV));
    }

    setService('');
    setDose('100');
    setSplit('1');
  };

  const price = useMemo(() => {
    if (boardStatusValues?.currentPh === boardStatusValues?.targetPh) {
      return Number(getTotalMtha);
    }

    return Number(getTotalMtha) * Number(mt.replace(/'/g, ''));
  }, [getTotalMtha, boardStatusValues, mt]);

  useEffect(() => {
    // When active, configure navigation buttons
    // and define the "Next" handler to update board state and trigger status handling
    if (!isActive) {
      return;
    }

    setShowBack(true);
    setShowNext(true);
    setDisabled(!!errorNv || !!errorMt || !!errorDose || mt.length < 1);
    setBackHandler(handleBackStep);

    setHandler(() => {
      setBoardSoilValues((prev: any) => ({
        ...prev,
        productName: productType,
        totalMtha: String(Number(getTotalMtha)),
        cost: String(price).slice(0, -3),
        dose: cleandose,
        split,
        selected: getSelectedValue,
        mt: cleanmt,
        service: cleanservice,
        secsmol: unMaskInputValue(boardSoilValues?.secsmol ?? ''),
        initialSecsmol: unMaskInputValue(boardSoilValues?.initialSecsmol ?? ''),
        nv: cleannv,
      }));

      handleNextStep();

      scrollTop();
    });
  }, [
    errorNv,
    errorMt,
    errorDose,
    isActive,
    mt.length,
    productType,
    boardSoilValues?.fineDrySoil,
    getTotalMtha,
    getFineDrySoil,
    cleandose,
    split,
    getSelectedValue,
    cleanmt,
    cleanservice,
    boardSoilValues?.secsmol,
    cleannv,
  ]);

  return (
    <form>
      <div className="border border-primitiveGrey p-8 md:py-2 md:px-4 rounded-2xl">
        <div className="border-b border-primitiveGrey pb-8">
          <div className="text-black font-bold text-xl md:text-lg mb-8">
            {isGreaterTarget ? t('improvementSoilPh') : t('maintainingSoilPh')}
          </div>
          <div className="flex mx-[-0.5rem] mb-8">
            <div className="w-1/3 md:w-full px-2">
              <Select
                options={productSelect}
                onChange={(value) => setProductType(value.value)}
                title={t('productName')}
                placeholder={t('selectValue')}
                value={
                  productType?.length > 0
                    ? [
                      {
                        value: productType,
                        label: productType,
                      },
                    ]
                    : []
                }
              />
            </div>
          </div>
          <div className="flex mx-[-0.5rem] mb-6">
            <div className="w-1/3 px-2">
              <MaskInput
                titleClassName="top-[-9px] left-[8px]"
                title={t('nv')}
                className="w-full"
                isGreyText={nv === initialNv}
                value={nv}
                onChange={setNv}
                redBorder={!!errorNv}
              />
              <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
                {errorNv && <div> {errorNv}</div>}
              </div>
            </div>
            <div className="w-1/3 px-2">
              <MaskInput
                titleClassName="top-[-9px] left-[8px] bg-white"
                title={`${currencyGreen}/${onlyMt}`}
                className="w-full"
                value={mt}
                onChange={setMt}
                redBorder={!!errorMt}
              />
              <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
                {errorMt && <div> {errorMt}</div>}
              </div>
            </div>
            <div className="w-1/3 px-2">
              <MaskInput
                titleClassName="top-[-9px] left-[8px]"
                title={t('service') + ` ${currencyGreen}/${onlyMt}`}
                className="w-full"
                value={numericValues(service).length > 0 ? service : ''}
                onChange={setService}
              />
            </div>
          </div>
          <div className="flex mx-[-0.5rem] mb-6">
            <div className="w-1/3 px-2">
              <MaskInput
                titleClassName="top-[-9px] left-[8px]"
                title={t('dose') + ' (%)'}
                className="w-full"
                value={dose}
                onChange={setDose}
                redBorder={!!errorDose}
              />
              <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
                {errorDose && <div> {errorDose}</div>}
              </div>
            </div>
            <div className="w-1/3 px-2">
              <Select
                titleClassName="truncate max-w-[85%]"
                options={splitOptions}
                placeholder={t('selectValue')}
                onChange={(value) => setSplit(value.value)}
                title={isGreaterTarget ? t('splitInYear') : t('frequency')}
                value={[
                  {
                    value: split,
                    label: split,
                  },
                ]}
              />
            </div>
            <div className="w-1/3 px-2">
              <MaskInput
                titleClassName="top-[-9px] left-[8px] bg-disable-gradient truncate max-w-[85%]"
                title={getMthaText}
                className="w-full"
                disabled
                // value={unMaskInputValue((Number(getMtha) / (Number(nv) / 100)).toFixed(2))}
                value={formatSwissNumber(Number(getMtha))}
              />
            </div>
          </div>
          <div className="text-black font-bold text-xl md:text-lg mb-[40px]">
            {t('carbonateAgronomic')}
          </div>
          <div className="flex mx-[-0.5rem]">
            <div className="w-1/3 px-2">
              <Input
                titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
                title={t('optimum')}
                className="w-full"
                disabled
                value={getOptimumValue}
              />
            </div>
            <div className="w-1/3 px-2">
              <Input
                titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
                title={t('selected')}
                className="w-full"
                disabled
                value={getSelectedValue}
              />
            </div>
            <div className="w-1/3 px-2">
              <Input
                titleClassName="top-[-9px] left-[8px] bg-disable-gradient truncate max-w-[85%]"
                title={t('efficiency') + ' (%)'}
                className="w-full"
                disabled
                value={
                  efficiencyValue !== 'Off'
                    ? Math.round(efficiencyValue) + ' %'
                    : efficiencyValue
                }
              />
            </div>
          </div>
        </div>

        <div className="py-8">
          <div className="text-black font-bold text-xl md:text-lg mb-8">
            {t('price')}
          </div>
          <div className="flex mx-[-0.5rem]">
            <div className="w-1/3 md:w-1/2 px-2">
              <MaskInput
                titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
                title={getTotalMthaText}
                className="w-full"
                disabled
                // value={unMaskInputValue((Number(getTotalMtha) / (Number(nv) / 100)).toFixed(2))}
                value={formatSwissNumber(Number(getTotalMtha))}
              />
            </div>
            <div className="w-1/3 md:w-1/2 px-2">
              <MaskInput
                titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
                title={t('cost') + ` (${currencyGreen}/${onlyAc})`}
                className="w-full"
                disabled
                value={
                  getFineDrySoil === 0
                    ? '0'
                    : '-' + formatSwissNumber(price)
                }
              />
            </div>
          </div>
        </div>

        {!isPDF && (
          <>
            <div className="flex items-center justify-center font-medium text-primary">
              <div
                className="cursor-pointer flex items-center"
                onClick={resetData}
              >
                <span className="mr-3">
                  <img src={resetIcon} alt="" />
                </span>
                {t('resetData')}
              </div>
            </div>
            <div className="text-[#FF3B30] text-xs ml-1 mt-3 mt-0 h-2 flex items-center justify-center mt-2">
              {mt.length < 1 && <div>{t('soilPriceDisable')}</div>}
            </div>
          </>
        )}
      </div>
    </form>
  );
};

export default SoilBoard;
